import React from "react";

/**
 * Privacy Policy — Vybly Dynamics Inc.
 * North star: TermsFeed-style policy you supplied, adapted for Vybly (AI use, 18+, public Hive‑Mind vs private reflections),
 * CPRA notice-at-collection, retention, and financial incentive. No sale/share; if that ever changes, you’ll update and provide rights.
 */
export default function PrivacyPolicy() {
  return (
    <main className="legal-page privacy" style={{ maxWidth: 960, margin: "40px auto", lineHeight: 1.6 }}>
      <h1>Privacy Policy</h1>
      <p><strong>Last updated:</strong> September 10, 2025</p>

      <p>
        This Privacy Policy describes our policies and procedures on the collection, use and disclosure of your information when you use
        the Service and tells you about your privacy rights and how the law protects you. We use your personal data to provide and improve
        the Service. By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy.
      </p>

      <h2>Interpretation and Definitions</h2>
      <h3>Interpretation</h3>
      <p>
        Words with the initial letter capitalized have meanings defined below. The following definitions shall have the same meaning regardless
        of whether they appear in singular or in plural.
      </p>

      <h3>Definitions</h3>
      <ul>
        <li><p><strong>Account</strong> means a unique account created for you to access our Service or parts of our Service.</p></li>
        <li><p><strong>Affiliate</strong> means an entity that controls, is controlled by or is under common control with a party, where “control”
          means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.</p></li>
        <li><p><strong>Business</strong>, for the purpose of CCPA/CPRA, refers to the Company as the legal entity that collects consumers’ personal information and
          determines the purposes and means of processing that information and does business in the State of California.</p></li>
        <li><p><strong>CCPA</strong> and/or <strong>CPRA</strong> refers to the California Consumer Privacy Act, as amended by the California Privacy Rights Act of 2020.</p></li>
        <li>
          <p><strong>Company</strong> (referred to as either “the Company”, “we”, “us” or “our”) refers to Vybly Dynamics Inc., a Delaware corporation operating from
          California, USA.</p>
          <p>For the purpose of the GDPR, the Company is the Data Controller.</p>
        </li>
        <li><p><strong>Consumer</strong> (CCPA/CPRA) means a natural person who is a California resident.</p></li>
        <li><p><strong>Cookies</strong> are small files placed on your device by a website, often used to remember your preferences or login session.</p></li>
        <li><p><strong>Country</strong> refers to California, United States.</p></li>
        <li><p><strong>Data Controller</strong> (GDPR) refers to the Company as the legal person which determines the purposes and means of processing personal data.</p></li>
        <li><p><strong>Device</strong> means any device that can access the Service such as a computer, phone or tablet.</p></li>
        <li><p><strong>Do Not Track (DNT)</strong> is a browser preference to limit cross‑site tracking.</p></li>
        <li><p><strong>GDPR</strong> refers to the EU/UK General Data Protection Regulation.</p></li>
        <li>
          <p><strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.</p>
          <p>Under GDPR: any information relating to you such as a name, identification number, location data, online identifier, or factors specific to identity.</p>
          <p>Under CCPA/CPRA: information that identifies, relates to, describes, is reasonably capable of being associated with, or could reasonably be linked to you.</p>
        </li>
        <li><p><strong>Service</strong> refers to the website and services we offer.</p></li>
        <li><p><strong>Service Provider</strong> means any natural or legal person who processes data on behalf of the Company (data processors).</p></li>
        <li><p><strong>Usage Data</strong> refers to data collected automatically, such as device identifiers, IP address, pages viewed and other diagnostic data.</p></li>
        <li><p><strong>Website</strong> refers to Vybly, accessible from <a href="https://www.vybly.net/" target="_blank" rel="noopener noreferrer">https://www.vybly.net/</a>.</p></li>
        <li>
          <p><strong>You</strong> means the individual accessing or using the Service (or the company or other legal entity on whose behalf the Service is used, as applicable).
            Under GDPR, you may also be referred to as the Data Subject or User.</p>
        </li>
      </ul>

      <h2>Eligibility (18+)</h2>
      <p>
        Our Service is intended for adults <strong>18 years and older</strong>. We do not knowingly collect personal data from individuals under 18.
        If we learn that a user under 18 has provided personal data, we will delete it.
      </p>

      <h2 id="notice">California “Notice at Collection” &amp; No Sale/Share</h2>
      <p>
        We collect the categories of personal information described in this policy for the purposes stated in <em>Use of Your Personal Data</em>.
        We <strong>do not “sell” or “share” personal information</strong> as defined by the CCPA/CPRA and we do not use or disclose Sensitive Personal Information
        for purposes other than those necessary to provide the Service, ensure security/fraud prevention, and comply with law. If that changes in the future, we will
        update this policy and provide required rights and opt‑outs <em>before</em> engaging in such activity.
      </p>

      <h2>Collecting and Using Your Personal Data</h2>
      <h3>Types of Data Collected</h3>
      <h4>Personal Data</h4>
      <p>While using our Service, we may ask you to provide certain personally identifiable information, including but not limited to:</p>
      <ul>
        <li>Email address</li>
        <li>Phone number</li>
        <li>Account identifiers (username); authentication data (passwords are hashed)</li>
        <li>User content you submit (e.g., reflections, prompts, Frameworks content, Hive‑Mind posts)</li>
      </ul>

      <h4>Usage Data</h4>
      <p>
        Usage Data is collected automatically when using the Service. It may include IP address, browser type and version, pages visited, time and date,
        time spent, device identifiers, crash data and diagnostic logs. When you access the Service on a mobile device, additional mobile identifiers
        and system information may be collected.
      </p>

      <h3>Tracking Technologies and Cookies</h3>
      <p>
        We use Cookies and similar technologies to operate the Service and remember your session. We primarily use <strong>necessary/functional</strong> cookies and
        <strong> first‑party analytics</strong>. If you disable cookies, certain features may not work. We do not use cross‑site behavioral advertising cookies.
      </p>
      <ul>
        <li><strong>Necessary/Essential</strong> (session auth, security) — administered by us.</li>
        <li><strong>Notice/Consent</strong> (record cookie choices) — administered by us.</li>
        <li><strong>Functionality</strong> (remember preferences) — administered by us.</li>
      </ul>

      <h3>AI Processing Transparency</h3>
      <p>
        To generate prompts or analyze text, we may transmit your inputs to AI service providers acting as our processors. We require them to protect
        your data and use it only to provide inference to us. <strong>We do not allow providers to train their foundation models on your reflections/Frameworks
        content</strong> unless we present a separate, clear consent flow and you opt in. AI systems can make mistakes; use your own judgment.
      </p>

      <h3>Public Hive‑Mind vs. Private Reflections</h3>
      <p>
        If you post in public areas (e.g., Hive‑Mind), those posts may be visible to other users and the public, indexed by search engines, and shared under the
        Service’s terms. Private reflections and Frameworks content are treated as private user content and are not published to other users unless you choose to share.
        Avoid submitting sensitive information where you do not want it to appear publicly.
      </p>

      <h3>Use of Your Personal Data</h3>
      <p>The Company may use personal data for the following purposes:</p>
      <ul>
        <li><strong>To provide and maintain the Service</strong>, including monitoring usage, ensuring availability, and personalizing prompts.</li>
        <li><strong>To manage your Account</strong> and provide features available to registered users.</li>
        <li><strong>To perform a contract</strong> (e.g., credits, memberships, bundles, one‑off charges, and subscriptions you purchase).</li>
        <li><strong>To contact you</strong> with service notices, security alerts, and transactional messages (e.g., receipts). Marketing emails are opt‑out.</li>
        <li><strong>To manage requests</strong> (support tickets, rights requests).</li>
        <li><strong>For business transfers</strong> (merger, acquisition, financing) with appropriate safeguards.</li>
        <li><strong>For other purposes</strong> such as analytics, product improvement, and safety/security (fraud, abuse prevention).</li>
      </ul>

      <h3>Sources of Personal Information</h3>
      <ul>
        <li><strong>Directly from you</strong> (forms, content you submit, purchases).</li>
        <li><strong>Automatically</strong> (usage and device data from your interactions with the Service).</li>
        <li><strong>Service Providers</strong> (payments, email delivery, hosting, analytics, AI inference).</li>
      </ul>

      <h3>Disclosure of Personal Data</h3>
      <ul>
        <li><strong>With Service Providers</strong> to operate the Service: hosting, AI inference, support, email (e.g., Mailgun), and payments (e.g., Stripe).</li>
        <li><strong>For business transfers</strong> as part of a merger, acquisition, or financing.</li>
        <li><strong>With Affiliates</strong> under this Privacy Policy.</li>
        <li><strong>With your consent/direction</strong>, including public Hive‑Mind posting.</li>
        <li><strong>Law enforcement/legal</strong> when required by law or to protect rights, property, and safety.</li>
      </ul>
      <p>We do <strong>not</strong> sell or share personal information under CPRA and do not disclose Sensitive Personal Information for secondary purposes.</p>

      <h3>Payments</h3>
      <p>
        We may provide paid products and/or services (e.g., session credits, memberships, subscriptions). Payments are processed by third‑party processors
        (e.g., Stripe). We do not store full card numbers. See Stripe’s privacy policy:{" "}
        <a href="https://stripe.com/us/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/us/privacy</a>.
      </p>

      <h3>Email &amp; Messaging</h3>
      <p>
        We may send you service and transactional emails. Marketing emails are optional and include an unsubscribe link. Email delivery may be handled by providers
        such as Mailgun (<a href="https://www.mailgun.com/legal/privacy-policy/" target="_blank" rel="noopener noreferrer">privacy policy</a>).
      </p>

      <h3>Retention</h3>
      <p>We retain information only as long as reasonably necessary for the purposes below, then delete or de‑identify it:</p>
      <ul>
        <li><strong>Account &amp; identifiers:</strong> life of account + up to 2 years (security, fraud, compliance).</li>
        <li><strong>Private reflections/Frameworks:</strong> life of account; deleted within 30 days after account deletion; backups roll off within ~45 days.</li>
        <li><strong>Public Hive‑Mind posts:</strong> retained as user‑generated content until removed by you or as required by law.</li>
        <li><strong>Usage logs:</strong> ~12–18 months for security/diagnostics.</li>
        <li><strong>Payment metadata:</strong> per tax/accounting laws (typically 7 years).</li>
      </ul>

      <h3>Transfer of Personal Data</h3>
      <p>
        Your information may be processed in the U.S. and other countries where our providers operate. Where required, we use appropriate safeguards
        (e.g., Standard Contractual Clauses) for cross‑border transfers.
      </p>

      <h3>Delete or Access Your Personal Data</h3>
      <p>
        You may request access, correction, or deletion of your personal data. Some information must be retained for legal obligations or legitimate interests
        (e.g., security, billing records).
      </p>

      <h2>GDPR Privacy</h2>
      <h3>Legal Bases</h3>
      <ul>
        <li><strong>Consent</strong> (e.g., optional marketing).</li>
        <li><strong>Contract</strong> (to provide the Service you request).</li>
        <li><strong>Legal obligation</strong> (tax, accounting, compliance).</li>
        <li><strong>Legitimate interests</strong> (security, product improvement, preventing fraud and misuse).</li>
      </ul>

      <h3>Your GDPR Rights</h3>
      <ul>
        <li>Access, correction, deletion, restriction, and portability.</li>
        <li>Object to processing based on legitimate interests or direct marketing.</li>
        <li>Withdraw consent where we rely on consent.</li>
        <li>Lodge a complaint with a supervisory authority.</li>
      </ul>

      <h2>CCPA/CPRA Privacy (California)</h2>
      <p>
        California residents have rights to know/access, correct, delete, and (where applicable) limit the use of Sensitive Personal Information.
        Because we do <strong>not</strong> sell or share personal information, there is no sale/share opt‑out at this time. To exercise rights,
        email <a href="mailto:info@vybly.net">info@vybly.net</a>. We will verify your request and respond as required by law.
      </p>

      <h2>Financial Incentive Disclosure</h2>
      <p>
        We may offer a promotional credit (e.g., one free session) when you create an account. Participation is optional and you may withdraw at any time
        by contacting <a href="mailto:info@vybly.net">info@vybly.net</a>. Such credits have no cash value and may be subject to expiration or other terms.
      </p>

      <h2>Do Not Track</h2>
      <p>The Service does not currently respond to DNT signals. You can manage cookie preferences in your browser settings.</p>

      <h2>Children’s Privacy</h2>
      <p>
        The Service is for adults 18+. We do not knowingly collect personal data from children under 13. If we become aware that a child under 13 has provided
        personal data, we will delete it.
      </p>

      <h2>Security</h2>
      <p>
        We use measures such as encryption in transit, restricted access, monitoring, and backups. No method of transmission or storage is 100% secure; if we
        learn of a breach, we will notify you as required by law.
      </p>

      <h2>Links to Other Websites</h2>
      <p>
        Our Service may contain links to websites not operated by us. We are not responsible for their content or practices. Please review the privacy policies
        of any third‑party sites you visit.
      </p>

      <h2>Translation Interpretation</h2>
      <p>
        This Privacy Policy may have been translated if we made it available to you on our Service.
        You agree that the original English text shall prevail in the case of a dispute.
      </p>

      <h2>Changes to this Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will post the new Privacy Policy on this page and update the “Last updated” date. For material
        changes, we will provide additional notice as appropriate.
      </p>

      <h2>Contact Us</h2>
      <p>
        Email: <a href="mailto:info@vybly.net">info@vybly.net</a><br />
      </p>
    </main>
  );
}
